/**
 * PostHog wiring — capture-by-default, cookieless, two-tier by design.
 *
 * The library is loaded lazily (dynamic import) only when a key is present,
 * so production bundles pay zero cost while analytics is unconfigured.
 * The MSF project key ships in .env.production (VITE_POSTHOG_KEY, id 151053, EU).
 *
 * ── TTDSG §25 + GDPR, two-tier (portfolio analytics standard) ──────────────
 * §25 TTDSG governs ONLY storing/reading information on the visitor's device
 * (cookies, localStorage). It does NOT govern counting a request. So:
 *
 *   DEFAULT (no consent decision yet) — BASIC, COOKIELESS, ANONYMOUS:
 *     persistence:"memory" + disable_persistence:true write NOTHING to the
 *     device, so §25 is not triggered and no consent is required. Page views +
 *     unique visitors are lawful under GDPR Art.6(1)(f) legitimate interest,
 *     minimised: EU-hosted, no persistent cross-session identifier, session
 *     recording OFF. Same basis cookieless analytics (Plausible/Fathom) rely
 *     on. We therefore capture by DEFAULT — no opt_out_capturing_by_default.
 *
 *   ON "Accept" (ConsentBanner) — PERSISTENT upgrade:
 *     persistence → "localStorage+cookie" (cross-session identity, funnel
 *     tracing) + session recording on. THAT device storage is what §25
 *     consent authorises.
 *
 *   ON "Decline" — opt out of everything (handled below + in the banner).
 *
 * person_profiles:"always" is required: with the default "identified_only",
 * posthog-js silently drops every anonymous pageview/autocapture (the internal
 * capture() gate short-circuits for un-identified visitors), which would blind
 * the basic reach tier we rely on.
 */
import type { CaptureResult } from 'posthog-js';
import type { PostHog } from 'posthog-js';
import { isPrerender } from '@/lib/utils/prerender';

/**
 * True when the page is being served from a local dev origin (Vite dev
 * server, a preview build on localhost, etc.), not a real visitor.
 * Ported from devotionfund/sportfund's posthog.ts after it measured 26% of a
 * 30-day pageview total coming from a local dev origin there, silently
 * inflating every traffic-derived decision.
 */
function isLocalDevHost(): boolean {
  if (typeof window === 'undefined') return false;
  const host = window.location.hostname;
  return host === 'localhost' || host === '127.0.0.1' || host.endsWith('.localhost');
}

/**
 * The portfolio's own Control Center uptime prober drives a real headless
 * browser against production with navigator.webdriver suppressed ("stealth"
 * mode meant to defeat bot detection), which defeats the navigator.webdriver
 * check in beforeSend() below. It still names itself in its UA string
 * ("... ConceptZoneProbe/1"). Ported from unifind (5cff866) after it measured
 * this inflating 30% of its 30-day pageviews.
 *
 * Tagged in beforeSend() rather than dropped at init (MSF-224, 2026-10-17):
 * this same prober IS the Control Center's "analytics-probe-<product>"
 * heartbeat — it visits every 6h and the heartbeat alarms if PostHog never
 * sees that visit. f7233d9 (2026-10-16) opted PostHog out entirely for this
 * UA, which silenced the heartbeat for good (confirmed: zero
 * ConceptZoneProbe-UA events in PostHog since the 10:52 UTC deploy, alarm
 * fired at "12.6h" — almost exactly 2 missed 6h cycles) while the live site
 * stayed up the whole time (prod-check.cjs 10/10). The 30% pageview
 * inflation this was meant to fix is already handled downstream: the hub's
 * own traffic figure only counts a visitor who fired a non-pageview event,
 * and scripts/posthog-real-visitors.cjs filters this exact UA substring at
 * query time — same outcome, without blinding the prober's own liveness
 * signal.
 */
function isKnownProbe(): boolean {
  if (typeof navigator === 'undefined') return false;
  return navigator.userAgent.includes('ConceptZoneProbe');
}

/**
 * AuthEmailHandler.tsx/PasswordReset.tsx carry Firebase's single-use `oobCode`
 * (password reset / email verification action code) as a URL query param —
 * exactly the shape a portfolio incident warned about (bankruptbuy shipped a
 * live sign-in token to PostHog via `/auth/click?t=TOKEN` because pageview
 * capture fires on load, before the token is consumed). Redact any query
 * param whose name looks like a credential before it leaves the browser,
 * everywhere a URL can end up in a captured property ($current_url, and
 * autocapture's per-element attr__href), not just the one page we know about
 * today.
 */
const SENSITIVE_QUERY_PARAM = /token|oobcode|secret/i;

function redactSensitiveQueryParams(value: string): string {
  if (!value.includes('?')) return value;
  try {
    const url = new URL(value, typeof window === 'undefined' ? undefined : window.location.origin);
    let changed = false;
    for (const key of Array.from(url.searchParams.keys())) {
      if (SENSITIVE_QUERY_PARAM.test(key)) {
        url.searchParams.set(key, 'redacted');
        changed = true;
      }
    }
    return changed ? url.toString() : value;
  } catch {
    return value;
  }
}

function redactUrlsInProperties(properties: Record<string, unknown> | undefined): void {
  if (!properties) return;
  for (const key of Object.keys(properties)) {
    const val = properties[key];
    if (typeof val === 'string') {
      properties[key] = redactSensitiveQueryParams(val);
    } else if (Array.isArray(val)) {
      // $elements (autocapture's clicked-element chain) is an array of
      // objects that may carry their own attr__href.
      for (const el of val) {
        if (el && typeof el === 'object') redactUrlsInProperties(el as Record<string, unknown>);
      }
    }
  }
}

/**
 * Signups and donate-flow events this run traced back to a sub-two-minute,
 * no-pause walk through signup -> dashboard -> own mission page -> donate
 * modal -> checkout_started (PostHog distinct_ids QXhu4YtpG4WnoUnDZclYktamILm1,
 * 8UBgNqD1sDcPCpKostj0qPuyi6M2, 2026-09-21/2026-10-06) -- consistent with a
 * puppeteer QA pass (this portfolio's executors occasionally launch one; see
 * CLAUDE.md) rather than a real student, and there is no property anywhere to
 * tell the two apart after the fact. navigator.webdriver is the one signal
 * every major headless driver (incl. puppeteer) sets and a real browser never
 * does, so tag it once here instead of re-deriving "is this signup real" by
 * hand from timestamps every time the question comes up.
 */
function beforeSend(cr: CaptureResult | null): CaptureResult | null {
  if (!cr) return cr;
  redactUrlsInProperties(cr.properties as Record<string, unknown> | undefined);
  redactUrlsInProperties(cr.$set as Record<string, unknown> | undefined);
  if (typeof navigator !== 'undefined' && navigator.webdriver && cr.properties) {
    cr.properties.$is_automated_browser = true;
  }
  if (isKnownProbe() && cr.properties) {
    cr.properties.$is_known_probe = true;
  }
  return cr;
}

/** First-party consent cookie — mirrors ConsentBanner.tsx. */
export const CONSENT_COOKIE = 'mystudyfund_consent';

/** Read the stored consent decision: "granted" | "denied" | null. */
function readConsent(): 'granted' | 'denied' | null {
  if (typeof document === 'undefined') return null;
  const m = document.cookie.match(/(?:^|;\s*)mystudyfund_consent=([^;]+)/);
  const v = m ? decodeURIComponent(m[1]) : null;
  return v === 'granted' || v === 'denied' ? v : null;
}

let phPromise: Promise<PostHog | null> | null = null;
/** distinct id we've already identified this session — prevents re-identify on every auth/user-doc snapshot */
let identifiedId: string | null = null;

export function initPostHog(): void {
  if (phPromise) return;
  // Never initialise during the static prerender/SSG pass. posthog-js injects
  // its bootstrap <script> into the DOM on init; the prerenderer would bake that
  // into every static page, re-running posthog.init() with DEFAULT cookie
  // persistence + session recording on real visits BEFORE consent — the exact
  // opposite of the cookieless-by-default guarantee above. (Also keeps the build
  // machine from emitting phantom pageviews.)
  if (isPrerender()) return;
  if (isLocalDevHost()) {
    phPromise = Promise.resolve(null);
    return;
  }
  const key = import.meta.env.VITE_POSTHOG_KEY as string | undefined;
  if (!key) {
    phPromise = Promise.resolve(null);
    return;
  }
  const host =
    (import.meta.env.VITE_POSTHOG_HOST as string | undefined) || 'https://eu.i.posthog.com';

  const consent = readConsent();

  phPromise = import('posthog-js')
    .then(({ default: posthog }) => {
      posthog.init(key, {
        // Auto-capture uncaught client-side exceptions (window.onerror +
        // unhandledrejection). There is no Sentry anywhere in this portfolio (that
        // decision is recorded in docs/portfolio-error-observability-decision), so
        // without this a page that crashes for a real user reports nothing at all
        // and the first symptom is the user leaving. The project-level
        // autocapture_exceptions_opt_in flag is NOT enough on its own: devotionfund
        // has it set and has captured zero, because its SDK init lacked this line.
        // Free at our volume — PostHog includes 100k exceptions/month.
        capture_exceptions: true,
        api_host: host,
        ui_host: 'https://eu.posthog.com',
        capture_pageview: 'history_change', // SPA route changes (kept: this is our pageview mechanism)
        // Override the "identified_only" default so anonymous pageviews are not
        // silently dropped — basic reach measurement needs every visitor.
        person_profiles: 'always',
        // Cookieless by default. persistence:"memory" alone still writes a
        // ph_<token>_posthog cookie, so disable_persistence:true is the hard
        // guarantee that nothing lands on the device pre-consent. On "Accept"
        // the banner flips both to the persistent tier.
        persistence: consent === 'granted' ? 'localStorage+cookie' : 'memory',
        disable_persistence: consent !== 'granted',
        disable_session_recording: consent !== 'granted',
        // opt_out_capturing() (below, and in ConsentBanner's decline()) stops
        // event capture but NOT feature-flag evaluation — posthog-js still
        // calls /flags on init and on every identify() regardless of consent
        // unless told not to. Ported from sportfund/devotionfund after that
        // leak was confirmed live there. Nothing in this app reads flags
        // today, so this is a pure removal of an unnecessary pre-consent
        // network call, not a behaviour change.
        advanced_disable_feature_flags_on_first_load: consent !== 'granted',
        // DO NOT set opt_out_capturing_by_default. DO NOT set respect_dnt.
        // Capture by DEFAULT (anonymous, cookieless).
        before_send: beforeSend,
      });

      if (consent === 'denied') {
        // Honour an explicit opt-out: stop all capture + wipe any stale storage
        // a previous visit may have left on the device, and stop the
        // feature-flag poller too (opt_out_capturing alone doesn't).
        posthog.opt_out_capturing();
        posthog.reset(true);
        posthog.featureFlags.setReloadingPaused(true);
      }

      // Compat: lib/utils/analytics.ts fires window.posthog?.capture(...)
      (window as unknown as { posthog: PostHog }).posthog = posthog;
      return posthog;
    })
    .catch((err) => {
      console.error('[posthog] init failed', err);
      return null;
    });
}

/**
 * Fire-and-forget capture that waits for the lazy posthog-js import instead of
 * reading window.posthog synchronously — the import is still in flight for a
 * signup that completes soon after page load, so a synchronous read silently
 * drops the event. Safe no-op while PostHog is unconfigured (phPromise null
 * during prerender, or resolves to null with no key / failed init).
 */
export function capture(event: string, properties?: Record<string, unknown>): void {
  void phPromise?.then((ph) => ph?.capture(event, properties));
}

/**
 * Ties events to the signed-in user. Safe no-op while PostHog is unconfigured.
 * Fires identify() at most once per distinct id per session — the auth listener
 * and the real-time user-doc snapshot both call this, so we guard against
 * re-identifying on every render / Firestore update.
 */
export function identifyUser(userId: string, properties?: Record<string, unknown>): void {
  if (!userId) return;
  if (identifiedId === userId) {
    // Already identified this session — the auth listener gets there first with
    // the uid alone, and the user-doc snapshot arrives later with the name and
    // roles. Setting them is not a second identify, so the guard above stays.
    if (properties) void phPromise?.then((ph) => ph?.setPersonProperties(properties));
    return;
  }
  identifiedId = userId;
  void phPromise?.then((ph) => ph?.identify(userId, properties));
}

/** Clears the identified user on sign-out. Safe no-op while unconfigured. */
export function resetUser(): void {
  identifiedId = null;
  void phPromise?.then((ph) => ph?.reset());
}
